import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx-js-style";
import { brandingLines, getBranding, getFooterLogo } from "./branding";

/*
 * One look for every export (PDF, print, Excel): neutral business graphite instead of the UI blue, the export date
 * always present, a bold labelled total, and the product logo in the bottom-right corner of PDF and print.
 *
 * A "section" is the common building block:
 *   { heading?, meta?: string[], head: string[], body: (string|number)[][], totalRow?: (string|number)[],
 *     decimals?: (number|null)[], ruled?: boolean, columnStyles?: object }
 * Numbers are formatted with the serbian locale (decimals per column, default 2) and right-aligned.
 */

const HEAD_FILL = [45, 55, 72]; // graphite
const TOTAL_FILL = [229, 231, 235];
const TEXT = [31, 41, 55];
const MUTED = [90, 98, 110];
const RULE = [156, 163, 175];
const MARGIN = 40;

/** "08.10.2026 23:36" - the moment of the export/print, shown on every document. */
/** "izvestaj.pdf" -> "izvestaj_2026-10-16_18-55.pdf": every export gets its own file name, so none overwrites or becomes "(1)". */
export function stampedFilename(filename, date = new Date()) {
  const p = (n) => String(n).padStart(2, "0");
  const stamp = `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}_${p(date.getHours())}-${p(date.getMinutes())}`;
  const dot = filename.lastIndexOf(".");
  return dot > 0 ? `${filename.slice(0, dot)}_${stamp}${filename.slice(dot)}` : `${filename}_${stamp}`;
}

export function formatStamp(date = new Date()) {
  const p = (n) => String(n).padStart(2, "0");
  return `${p(date.getDate())}.${p(date.getMonth() + 1)}.${date.getFullYear()} ${p(date.getHours())}:${p(date.getMinutes())}`;
}

function fmtCell(value, decimals) {
  if (typeof value !== "number") return value ?? "";
  const d = decimals ?? 2;
  return value.toLocaleString("sr-RS", { minimumFractionDigits: d, maximumFractionDigits: d });
}

const isNumberColumn = (section, ci) => section.body.some((r) => typeof r[ci] === "number") || typeof section.totalRow?.[ci] === "number";

/** Sums every column that holds only numbers; the first column gets the label. Null when there is nothing to sum. */
export function buildTotalRow(head, body, label = "Ukupno") {
  const sums = head.map((_, ci) => {
    const col = body.map((r) => r[ci]);
    return col.length > 0 && col.every((v) => typeof v === "number") ? Math.round(col.reduce((a, b) => a + b, 0) * 1000) / 1000 : null;
  });
  if (sums.every((v) => v === null)) return null;
  return head.map((_, ci) => (ci === 0 ? label : sums[ci] === null ? "" : sums[ci]));
}

// ---------------------------------------------------------------------------------------------------- Excel

const thin = (rgb) => ({ style: "thin", color: { rgb } });
const XL_HEAD = { font: { bold: true, color: { rgb: "FFFFFF" } }, fill: { fgColor: { rgb: "2D3748" } }, alignment: { vertical: "center" } };
const XL_TOTAL = { font: { bold: true }, fill: { fgColor: { rgb: "E5E7EB" } }, border: { top: thin("374151"), bottom: thin("374151") } };

function excelSheet({ lines = [], title, sections, grandRow, grandDecimals }) {
  const aoa = []; // rows of { v, s, z }
  const push = (cells) => aoa.push(cells);
  const c = (v, s, z) => ({ v: v ?? "", s, z });

  lines.forEach((l, i) => push([c(l, i === 0 ? { font: { bold: true, sz: 12 } } : undefined)]));
  if (lines.length) push([]);
  if (title) push([c(title, { font: { bold: true, sz: 14 } })]);
  push([c(`Datum izvoza: ${formatStamp()}`, { font: { color: { rgb: "5A626E" } } })]);
  push([]);

  let cols = 1;
  sections.forEach((sec) => {
    cols = Math.max(cols, sec.head.length);
    if (sec.heading) push([c(sec.heading, { font: { bold: true, sz: 12 } })]);
    if (sec.meta?.length) push([c(sec.meta.join("     "), { font: { color: { rgb: "5A626E" } } })]);
    push(sec.head.map((h, ci) => c(h, { ...XL_HEAD, alignment: { vertical: "center", horizontal: isNumberColumn(sec, ci) ? "right" : "left" } })));
    sec.body.forEach((row) =>
      push(
        sec.head.map((_, ci) => {
          const v = row[ci];
          return typeof v === "number" ? c(v, undefined, (sec.decimals?.[ci] ?? 2) === 0 ? "#,##0" : "#,##0.00") : c(v);
        })
      )
    );
    if (sec.totalRow) {
      push(
        sec.head.map((_, ci) => {
          const v = sec.totalRow[ci];
          return typeof v === "number" ? c(v, XL_TOTAL, (sec.decimals?.[ci] ?? 2) === 0 ? "#,##0" : "#,##0.00") : c(v, XL_TOTAL);
        })
      );
    }
    push([]);
  });

  if (grandRow) {
    const width = Math.max(cols, grandRow.length);
    push(
      Array.from({ length: width }, (_, ci) => {
        const v = grandRow[ci];
        return typeof v === "number" ? c(v, XL_TOTAL, (grandDecimals?.[ci] ?? 2) === 0 ? "#,##0" : "#,##0.00") : c(v, XL_TOTAL);
      })
    );
  }

  const ws = {};
  let maxCols = 0;
  aoa.forEach((row, r) =>
    row.forEach((cell, ci) => {
      maxCols = Math.max(maxCols, ci + 1);
      const ref = XLSX.utils.encode_cell({ r, c: ci });
      const isNum = typeof cell.v === "number";
      ws[ref] = { v: cell.v, t: isNum ? "n" : "s" };
      if (cell.s) ws[ref].s = cell.s;
      if (cell.z) ws[ref].z = cell.z;
    })
  );
  ws["!ref"] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: Math.max(aoa.length - 1, 0), c: Math.max(maxCols - 1, 0) } });
  // Column width follows the content of the data rows (the long company header lines are not taken into account).
  const widths = Array.from({ length: maxCols }, () => 10);
  sections.forEach((sec) => {
    [sec.head, ...sec.body, sec.totalRow].filter(Boolean).forEach((row) =>
      row.forEach((v, ci) => {
        const len = typeof v === "number" ? 14 : String(v ?? "").length;
        widths[ci] = Math.min(48, Math.max(widths[ci] || 10, len + 2));
      })
    );
  });
  ws["!cols"] = widths.map((wch) => ({ wch }));
  return ws;
}

async function writeExcel({ title, sections, grandRow, grandDecimals, filename, sheetName }) {
  // Company header (text from the ini table) above the data; Excel does not carry an image here.
  const { info } = await getBranding();
  const ws = excelSheet({ lines: brandingLines(info), title, sections, grandRow, grandDecimals });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, String(sheetName || "Izveštaj").substring(0, 31));
  XLSX.writeFile(wb, stampedFilename(filename));
}

/**
 * Export an array of objects to a single Excel sheet. A bold "Ukupno" row sums every numeric column
 * (rows must therefore hold real numbers, not formatted text). totals: false turns it off.
 */
export function exportToExcel(rows, filename, sheetName = "Izveštaj", { totals = true, title } = {}) {
  const head = rows.length ? Object.keys(rows[0]) : [];
  const body = rows.map((r) => head.map((k) => r[k]));
  const totalRow = totals ? buildTotalRow(head, body) : null;
  return writeExcel({ title, sections: [{ head, body, totalRow }], filename, sheetName });
}

/** Several blocks on one sheet (e.g. one block per table, each with its own subtotal) and a bold grand total at the end. */
export function exportSectionsToExcel({ title, sections, grandRow, grandDecimals, filename, sheetName }) {
  return writeExcel({ title, sections, grandRow, grandDecimals, filename, sheetName });
}

// ------------------------------------------------------------------------------------------------------ PDF

async function createDoc() {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  // jsPDF's built-in fonts have no č, ć, đ: embed a subset of DejaVu Sans (loaded on demand).
  const { DEJAVU_REGULAR, DEJAVU_BOLD } = await import("./pdfFonts");
  doc.addFileToVFS("DejaVuSans.ttf", DEJAVU_REGULAR);
  doc.addFont("DejaVuSans.ttf", "DejaVu", "normal");
  doc.addFileToVFS("DejaVuSans-Bold.ttf", DEJAVU_BOLD);
  doc.addFont("DejaVuSans-Bold.ttf", "DejaVu", "bold");
  doc.setFont("DejaVu", "normal");
  return doc;
}

const imgFormat = (mime) => (/png/i.test(mime) ? "PNG" : /webp/i.test(mime) ? "WEBP" : "JPEG");

function drawHeader(doc, { info, logo, title, stamp }) {
  const pageW = doc.internal.pageSize.getWidth();
  let textX = MARGIN;
  let headerBottom = MARGIN;
  if (logo) {
    const scale = Math.min(130 / logo.width, 48 / logo.height);
    const w = logo.width * scale;
    const h = logo.height * scale;
    try {
      doc.addImage(logo.dataUrl, imgFormat(logo.mime), MARGIN, 32, w, h);
      textX = MARGIN + w + 14;
      headerBottom = 32 + h;
    } catch {
      /* a broken logo must not break the export */
    }
  }
  const lines = brandingLines(info);
  if (lines.length) {
    let y = 42;
    lines.forEach((line, idx) => {
      doc.setFontSize(idx === 0 ? 11 : 8.5);
      doc.setFont("DejaVu", idx === 0 ? "bold" : "normal");
      doc.setTextColor(...TEXT);
      doc.text(line, textX, y);
      y += idx === 0 ? 13 : 11;
    });
    headerBottom = Math.max(headerBottom, y - 6);
  }
  let y = MARGIN;
  if (lines.length || logo) {
    doc.setDrawColor(...RULE);
    doc.line(MARGIN, headerBottom + 6, pageW - MARGIN, headerBottom + 6);
    y = headerBottom + 28;
  } else {
    y = MARGIN + 6;
  }
  if (title) {
    doc.setFont("DejaVu", "bold");
    doc.setFontSize(15);
    doc.setTextColor(...TEXT);
    doc.text(title, MARGIN, y);
  }
  doc.setFont("DejaVu", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  doc.text(`Datum štampe: ${stamp}`, pageW - MARGIN, y, { align: "right" });
  doc.setTextColor(...TEXT);
  return y + 18;
}

/** Date + page number on the left, the product logo in the bottom-right corner, on every page. */
function drawFooters(doc, { stamp, footerLogo }) {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setDrawColor(...RULE);
    doc.line(MARGIN, pageH - 44, pageW - MARGIN, pageH - 44);
    doc.setFont("DejaVu", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(`Odštampano: ${stamp}`, MARGIN, pageH - 26);
    doc.text(`Strana ${i} od ${total}`, pageW / 2, pageH - 26, { align: "center" });
    if (footerLogo) {
      const scale = Math.min(62 / footerLogo.width, 15 / footerLogo.height);
      const w = footerLogo.width * scale;
      const h = footerLogo.height * scale;
      try {
        doc.addImage(footerLogo.dataUrl, imgFormat(footerLogo.mime), pageW - MARGIN - w, pageH - 14 - h, w, h);
      } catch {
        /* no logo in the footer, the export still works */
      }
    }
  }
}

function pdfTable(doc, sec, startY) {
  const body = sec.body.map((r) => sec.head.map((_, ci) => fmtCell(r[ci], sec.decimals?.[ci])));
  const foot = sec.totalRow ? [sec.head.map((_, ci) => fmtCell(sec.totalRow[ci], sec.decimals?.[ci]))] : undefined;
  const columnStyles = { ...(sec.columnStyles || {}) };
  sec.head.forEach((_, ci) => {
    if (isNumberColumn(sec, ci)) columnStyles[ci] = { halign: "right", ...(columnStyles[ci] || {}) };
  });
  autoTable(doc, {
    startY,
    head: [sec.head],
    body,
    foot,
    showFoot: "lastPage",
    theme: sec.ruled ? "plain" : "grid",
    margin: { left: MARGIN, right: MARGIN, bottom: 60 },
    styles: { font: "DejaVu", fontSize: 10, textColor: TEXT, lineColor: RULE, lineWidth: sec.ruled ? 0 : 0.5, cellPadding: sec.ruled ? 6 : 5 },
    headStyles: { fillColor: HEAD_FILL, textColor: 255, fontStyle: "bold", lineWidth: 0 },
    footStyles: { fillColor: TOTAL_FILL, textColor: TEXT, fontStyle: "bold", lineWidth: sec.ruled ? 0 : 0.5 },
    columnStyles,
    // Header and total cells of number columns are right-aligned like the numbers under them.
    didParseCell: (data) => {
      if ((data.section === "head" || data.section === "foot") && isNumberColumn(sec, data.column.index)) data.cell.styles.halign = "right";
    },
    // "Ruled" lists (shopping list): every row underlined.
    didDrawCell: sec.ruled
      ? (data) => {
          if (data.section === "body") {
            doc.setDrawColor(...TEXT);
            doc.setLineWidth(0.6);
            doc.line(data.cell.x, data.cell.y + data.cell.height, data.cell.x + data.cell.width, data.cell.y + data.cell.height);
          }
        }
      : undefined,
  });
  return doc.lastAutoTable.finalY;
}

/** PDF made of one or more sections, with the company header, print date and the logo in the bottom-right corner. */
export async function exportSectionsToPDF({ title, sections, grandRow, grandDecimals, filename }) {
  const [{ info, logo }, footerLogo, doc] = await Promise.all([getBranding(), getFooterLogo(), createDoc()]);
  const stamp = formatStamp();
  const pageH = doc.internal.pageSize.getHeight();
  let y = drawHeader(doc, { info, logo, title, stamp });

  sections.forEach((sec) => {
    if (sec.heading) {
      if (y > pageH - 150) {
        doc.addPage();
        y = MARGIN + 6;
      }
      doc.setFont("DejaVu", "bold");
      doc.setFontSize(12);
      doc.setTextColor(...TEXT);
      doc.text(sec.heading, MARGIN, y);
      y += 14;
      if (sec.meta?.length) {
        doc.setFont("DejaVu", "normal");
        doc.setFontSize(9);
        doc.setTextColor(...MUTED);
        doc.text(sec.meta.join("     "), MARGIN, y);
        y += 8;
      }
      doc.setTextColor(...TEXT);
    }
    y = pdfTable(doc, sec, y + 4) + 22;
  });

  if (grandRow) {
    if (y > pageH - 110) {
      doc.addPage();
      y = MARGIN + 6;
    }
    autoTable(doc, {
      startY: y - 8,
      body: [grandRow.map((v, ci) => fmtCell(v, grandDecimals?.[ci]))],
      theme: "plain",
      margin: { left: MARGIN, right: MARGIN, bottom: 60 },
      styles: { font: "DejaVu", fontStyle: "bold", fontSize: 11, textColor: 255, fillColor: HEAD_FILL, cellPadding: 7 },
      columnStyles: { [grandRow.length - 1]: { halign: "right" } },
    });
  }

  drawFooters(doc, { stamp, footerLogo });
  doc.save(stampedFilename(filename));
}

/** A single table to PDF, with an optional "Ukupno" row at the end (kept for the report pages). */
export function exportToPDF({ title, head, body, filename, totalsRow, decimals, ruled, columnStyles }) {
  return exportSectionsToPDF({ title, filename, sections: [{ head, body, totalRow: totalsRow, decimals, ruled, columnStyles }] });
}

// ----------------------------------------------------------------------------------------------------- print

const escapeHtml = (t) => String(t ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

/** HTML for sections (used by print): heading, meta line, table with a bold total row. */
export function sectionsToHtml(sections, grandRow, grandDecimals) {
  const cell = (v, d, tag = "td", extra = "") => `<${tag} class="${typeof v === "number" ? "num" : ""}${extra}">${escapeHtml(fmtCell(v, d))}</${tag}>`;
  const html = sections
    .map((sec) => {
      const head = `<tr>${sec.head.map((h, ci) => `<th class="${isNumberColumn(sec, ci) ? "num" : ""}">${escapeHtml(h)}</th>`).join("")}</tr>`;
      const body = sec.body.map((r) => `<tr>${sec.head.map((_, ci) => cell(r[ci], sec.decimals?.[ci])).join("")}</tr>`).join("");
      const foot = sec.totalRow
        ? `<tfoot><tr class="total">${sec.head
            .map((_, ci) => `<td class="${typeof sec.totalRow[ci] === "number" ? "num" : ""}">${escapeHtml(fmtCell(sec.totalRow[ci], sec.decimals?.[ci]))}</td>`)
            .join("")}</tr></tfoot>`
        : "";
      return `<section class="${sec.body.length <= 12 ? "keep" : ""}">
        ${sec.heading ? `<h3>${escapeHtml(sec.heading)}</h3>` : ""}
        ${sec.meta?.length ? `<p class="meta">${sec.meta.map(escapeHtml).join("<span class='gap'></span>")}</p>` : ""}
        <table class="${sec.ruled ? "ruled" : ""}"><thead>${head}</thead><tbody>${body}</tbody>${foot}</table>
      </section>`;
    })
    .join("");
  const grand = grandRow
    ? `<table class="grand"><tbody><tr>${grandRow.map((v, ci) => `<td class="${typeof v === "number" ? "num" : ""}">${escapeHtml(fmtCell(v, grandDecimals?.[ci]))}</td>`).join("")}</tr></tbody></table>`
    : "";
  return html + grand;
}

/**
 * Opens a popup window and prints the given HTML with the company header, the print date and the logo in the bottom-right
 * corner. The window is opened IMMEDIATELY (in the click handler) and only then waits for the company data, so the
 * browser does not block the popup.
 */
function openPrintWindow(bodyHtml, { title = "Izveštaj" } = {}) {
  const printWindow = window.open("", "", "width=900,height=700");
  if (!printWindow) {
    window.alert("Browser je blokirao popup prozor za štampu. Dozvoli popup-e za ovu stranicu.");
    return;
  }
  Promise.all([getBranding(), getFooterLogo()]).then(([{ info, logo }, footerLogo]) => {
    const stamp = formatStamp();
    const lines = brandingLines(info);
    const header =
      lines.length || logo
        ? `<div class="firm">
            ${logo ? `<img class="logo" src="${logo.dataUrl}" alt="" />` : ""}
            <div>${lines.map((l, i) => (i === 0 ? `<strong>${escapeHtml(l)}</strong>` : `<div class="sub">${escapeHtml(l)}</div>`)).join("")}</div>
          </div>`
        : "";
    printWindow.document.write(`
    <html>
      <head>
        <meta charset="utf-8" />
        <title>${escapeHtml(title)}</title>
        <style>
          /* margin 0 + spacer rows: the browser prints no URL/date of its own, and the fixed footer never collides with content */
          @page { size: A4; margin: 0 0 9mm 0;
            @bottom-center { content: "Strana " counter(page) " / " counter(pages); font: 11px Geist, "Segoe UI", Arial, sans-serif; color: #5A626E; } }
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          body { font-family: Geist, "Segoe UI", system-ui, Arial, sans-serif; margin: 0; padding: 0 12mm; color: #1F2937; }
          table.frame { width: 100%; border-collapse: collapse; }
          table.frame > thead > tr > td { height: 14mm; padding: 0; border: none; }
          table.frame > tfoot > tr > td { height: 26mm; padding: 0; border: none; }
          table.frame > tbody > tr > td { padding: 0; border: none; vertical-align: top; }
          .firm { display: flex; align-items: center; gap: 14px; padding-bottom: 10px; margin-bottom: 14px; border-bottom: 1px solid #9CA3AF; }
          .firm .logo { max-height: 56px; max-width: 160px; object-fit: contain; }
          .firm strong { font-size: 15px; }
          .firm .sub { font-size: 12px; color: #5A626E; margin-top: 2px; }
          .titlebar { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; margin: 0 0 14px; }
          h2 { margin: 0; font-size: 20px; color: #1F2937; }
          .stamp { font-size: 12px; color: #5A626E; white-space: nowrap; }
          h3 { margin: 18px 0 2px; font-size: 15px; color: #1F2937; }
          .meta { margin: 0 0 6px; font-size: 12px; color: #5A626E; }
          .meta .gap { display: inline-block; width: 22px; }
          section { margin-bottom: 14px; }
          section.keep { break-inside: avoid; }
          .content table { width: 100%; border-collapse: collapse; }
          .content th, .content td { border: 1px solid #9CA3AF; padding: 7px 8px; text-align: left; font-size: 13px; }
          .content th { background: #2D3748; color: #fff; font-weight: 600; }
          .content .num { text-align: right; font-variant-numeric: tabular-nums; }
          .content tr { break-inside: avoid; }
          .content thead { display: table-header-group; }
          .content tr.total td { font-weight: 700; background: #E5E7EB; border-top: 2px solid #374151; }
          .content table.grand td { background: #2D3748; color: #fff; font-weight: 700; font-size: 14px; border-color: #2D3748; }
          .content table.ruled th, .content table.ruled td { border: none; }
          .content table.ruled td { border-bottom: 1px solid #1F2937; padding: 9px 8px; }
          .footer { position: fixed; left: 12mm; right: 12mm; bottom: 11mm; display: flex; justify-content: space-between; align-items: flex-end;
            border-top: 1px solid #9CA3AF; padding-top: 4px; font-size: 11px; color: #5A626E; }
          .footer img { height: 16px; max-width: 80px; object-fit: contain; }
        </style>
      </head>
      <body>
        <table class="frame">
          <thead><tr><td></td></tr></thead>
          <tfoot><tr><td></td></tr></tfoot>
          <tbody><tr><td>
            ${header}
            <div class="titlebar"><h2>${escapeHtml(title)}</h2><span class="stamp">Datum štampe: ${stamp}</span></div>
            <div class="content">${bodyHtml}</div>
          </td></tr></tbody>
        </table>
        <div class="footer"><span>Odštampano: ${stamp}</span>${footerLogo ? `<img src="${footerLogo.dataUrl}" alt="" />` : "<span></span>"}</div>
        <script>
          window.onload = function () {
            window.print();
            window.onafterprint = function () { window.close(); };
          };
        </script>
      </body>
    </html>
  `);
    printWindow.document.close();
  });
}

/** Prints an existing DOM element (e.g. a ref to <table>) as a standalone, clean page. */
export function printElement(element, opts) {
  if (!element) return;
  openPrintWindow(element.outerHTML, opts);
}

/** Prints hand-built HTML (when there is no single DOM element, e.g. several tables in a row). */
export function printHtml(bodyHtml, opts) {
  openPrintWindow(bodyHtml, opts);
}

/** Prints sections (same data as the PDF/Excel export of the same page). */
export function printSections({ title, sections, grandRow, grandDecimals }) {
  openPrintWindow(sectionsToHtml(sections, grandRow, grandDecimals), { title });
}
